import z from 'zod';
import type { Agent } from '../agent';
import { ModelBehaviorError, ModelRefusalError } from '../errors';
import {
  type RunItem,
  RunMessageOutputItem,
  RunToolApprovalItem,
} from '../items';
import type { ModelResponse } from '../model';
import type { RunConfig, Runner, ToolErrorFormatter } from '../run';
import type { RunState } from '../run-state';
import type { FunctionToolResult } from '../tool';
import { getFunctionToolQualifiedName } from '../tool-identity';
import { addErrorToCurrentSpan } from '../tracing/context';
import type { AgentInputItem } from '../types';
import * as protocol from '../types/protocol';
import * as ProviderData from '../types/provider-data';
import {
  getRefusalFromOutputMessage,
  getTextFromOutputMessage,
} from '../utils/messages';
import { safeExecute } from '../utils/safe-execute';
import { getSchemaAndParserFromInputType } from '../utils/tools';
import {
  createRunErrorFinalOutputItem,
  formatRunErrorFinalOutput,
  type RunErrorData,
  type RunErrorHandlers,
  resolveRunErrorHandler,
} from './error-handlers';
import { getTurnInput } from './items';
import { handleHostedMcpApprovals } from './mcp-approvals';
import { SingleStepResult } from './steps';
import {
  checkForFinalOutputFromTools,
  collectInterruptions,
  executeFunctionToolCalls,
  executeHandoffCalls,
} from './tool-execution';
import type { ProcessedResponse, ToolRunHandoff } from './types';

const APPROVAL_ITEM_TYPES = ['function_call', 'hosted_tool_call'] as const;

type ApprovalItemLike =
  | RunToolApprovalItem
  | {
      rawItem?: protocol.FunctionCallItem | protocol.HostedToolCallItem;
      agent?: Agent<any, any>;
    };

/**
 * @internal
 * Continues a turn that was previously interrupted waiting for tool approval. Executes the now
 * approved tools and returns the resulting step transition.
 */
export async function resolveInterruptedTurn<TContext>(
  agent: Agent<TContext, any>,
  originalInput: string | AgentInputItem[],
  originalPreStepItems: RunItem[],
  newResponse: ModelResponse,
  processedResponse: ProcessedResponse,
  runner: Runner,
  state: RunState<TContext, Agent<TContext, any>>,
  toolErrorFormatter?: ToolErrorFormatter,
  agentToolParentRunConfig?: Partial<RunConfig>,
): Promise<SingleStepResult> {
  // call_ids for function tools
  const functionCallIds = originalPreStepItems
    .filter(
      (item) =>
        item instanceof RunToolApprovalItem &&
        'callId' in item.rawItem &&
        item.rawItem.type === 'function_call',
    )
    .map((item) => (item.rawItem as protocol.FunctionCallItem).callId);

  const completedFunctionCallIds = collectCompletedCallIds(
    originalPreStepItems,
    'function_call_result',
  );

  // We already persisted the turn once when the approval interrupt was raised, so the
  // counter reflects the approval items as "flushed". When we resume the same turn we need
  // to rewind it so the eventual tool output for this call is still written to the session.
  const pendingApprovalItems = state
    .getInterruptions()
    .filter(isApprovalItemLike);

  const pendingApprovalIdentities = new Set<string>();
  for (const approval of pendingApprovalItems) {
    if (!(approval instanceof RunToolApprovalItem)) {
      continue;
    }
    if (isHostedMcpApprovalItem(approval)) {
      continue;
    }
    const rawItem = approval.rawItem;
    if (
      rawItem.type === 'function_call' &&
      rawItem.callId &&
      completedFunctionCallIds.has(rawItem.callId)
    ) {
      continue;
    }
    const identity = getApprovalIdentity(approval);
    if (identity) {
      if (resolveApprovalState(approval, state) === 'pending') {
        pendingApprovalIdentities.add(identity);
      }
    }
  }
  // Run function tools that require approval or are resuming a nested agent tool run.
  const functionToolRuns = processedResponse.functions.filter((run) => {
    const callId = run.toolCall.callId;
    if (!callId) {
      return false;
    }
    const isApprovedCall = functionCallIds.includes(callId);
    const isPendingNested = state.hasPendingAgentToolRun(
      getFunctionToolQualifiedName(run.tool) ?? run.tool.name,
      callId,
    );
    if (!isApprovedCall && !isPendingNested) {
      return false;
    }
    return !completedFunctionCallIds.has(callId);
  });

  const functionResults = await executeFunctionToolCalls(
    agent,
    functionToolRuns,
    runner,
    state,
    toolErrorFormatter,
    agentToolParentRunConfig,
  );

  const newItems: RunItem[] = [];
  const appendContext = buildAppendContext(originalPreStepItems);
  const appendIfNew = (item: RunItem) =>
    appendRunItemIfNew(item, newItems, appendContext);

  for (const result of functionResults) {
    if (
      result.type === 'function_output' &&
      Array.isArray(result.interruptions) &&
      result.interruptions.length > 0
    ) {
      continue;
    }
    appendIfNew(result.runItem);
  }

  const additionalInterruptions = collectInterruptions([], []);

  const hostedMcpApprovals = await handleHostedMcpApprovals({
    requests: processedResponse.mcpApprovalRequests,
    agent,
    state,
    functionResults,
    appendIfNew,
    resolveApproval: (rawItem) => {
      const providerData =
        rawItem.providerData as ProviderData.HostedMCPApprovalRequest;
      const approvalRequestId = rawItem.id ?? providerData?.id;
      if (!approvalRequestId) {
        return undefined;
      }
      return state._context.isToolApproved({
        toolName: rawItem.name,
        callId: approvalRequestId,
      });
    },
  });

  // Server-managed conversations rely on preStepItems to re-surface pending approvals.
  // Keep unresolved hosted MCP approvals in place so HITL flows still have something to approve next turn.
  // Drop resolved approval placeholders so they are not replayed on the next turn, but keep
  // pending approvals in place to signal the outstanding work to the UI and session store.
  const preStepItems = originalPreStepItems.filter((item) => {
    if (!(item instanceof RunToolApprovalItem)) {
      return true;
    }

    if (isHostedMcpApprovalItem(item)) {
      if (hostedMcpApprovals.pendingApprovals.has(item)) {
        return true;
      }
      const approvalRequestId =
        item.rawItem.id ??
        (
          item.rawItem.providerData as
            | ProviderData.HostedMCPApprovalRequest
            | undefined
        )?.id;
      if (approvalRequestId) {
        return hostedMcpApprovals.pendingApprovalIds.has(approvalRequestId);
      }
      return false;
    }

    // Preserve all other approval items so resumptions can continue to reference the
    // original approval requests (e.g., function/shell) ONLY while they are still pending.
    const identity = getApprovalIdentity(item);
    if (!identity) {
      return true;
    }
    return pendingApprovalIdentities.has(identity);
  });

  const keptApprovalItems = new Set<RunToolApprovalItem>();
  for (const item of preStepItems) {
    if (item instanceof RunToolApprovalItem) {
      keptApprovalItems.add(item);
    }
  }
  let removedApprovalCount = 0;
  for (const item of originalPreStepItems) {
    if (item instanceof RunToolApprovalItem && !keptApprovalItems.has(item)) {
      removedApprovalCount++;
    }
  }
  if (removedApprovalCount > 0) {
    // Persisting the approval request already advanced the counter once, so undo the increment
    // to make sure we write the final tool output back to the session when the turn resumes.
    state.rewindTurnPersistence(removedApprovalCount);
  }

  const completedStep = await maybeCompleteTurnFromToolResults({
    agent,
    runner,
    state,
    functionResults,
    originalInput,
    newResponse,
    preStepItems,
    newItems,
    additionalInterruptions,
  });

  if (completedStep) {
    return completedStep;
  }

  // we only ran new tools and side effects. We need to run the rest of the agent
  return new SingleStepResult(
    originalInput,
    newResponse,
    preStepItems,
    newItems,
    { type: 'next_step_run_again' },
  );
}

function collectCompletedCallIds(items: RunItem[], type: string): Set<string> {
  const completed = new Set<string>();
  for (const item of items) {
    const rawItem = item.rawItem;
    if (!rawItem || typeof rawItem !== 'object') {
      continue;
    }
    if ((rawItem as { type?: string }).type !== type) {
      continue;
    }
    const callId = (rawItem as { callId?: unknown }).callId;
    if (typeof callId === 'string') {
      completed.add(callId);
    }
  }
  return completed;
}

function isApprovalItemLike(value: unknown): value is ApprovalItemLike {
  if (!value || typeof value !== 'object') {
    return false;
  }

  if (!('rawItem' in value)) {
    return false;
  }

  const rawItem = (value as { rawItem?: unknown }).rawItem;
  if (!rawItem || typeof rawItem !== 'object') {
    return false;
  }

  const itemType = (rawItem as { type?: unknown }).type;
  return APPROVAL_ITEM_TYPES.includes(
    itemType as (typeof APPROVAL_ITEM_TYPES)[number],
  );
}

function isHostedMcpApprovalItem(item: RunToolApprovalItem): boolean {
  return (
    item.rawItem.type === 'hosted_tool_call' &&
    item.rawItem.providerData?.type === 'mcp_approval_request'
  );
}

function getApprovalIdentity(approval: ApprovalItemLike): string | undefined {
  const rawItem = approval.rawItem;
  if (!rawItem) {
    return undefined;
  }

  if (rawItem.type === 'function_call' && rawItem.callId) {
    return `function_call:${rawItem.callId}`;
  }

  if ('callId' in rawItem && rawItem.callId) {
    return `${rawItem.type}:${rawItem.callId}`;
  }

  const id = 'id' in rawItem ? rawItem.id : undefined;
  if (id) {
    return `${rawItem.type}:${id}`;
  }

  const providerData =
    typeof rawItem.providerData === 'object' && rawItem.providerData
      ? (rawItem.providerData as { id?: string })
      : undefined;
  if (providerData?.id) {
    return `${rawItem.type}:provider:${providerData.id}`;
  }

  const agentName =
    'agent' in approval && approval.agent ? approval.agent.name : '';

  try {
    return `${agentName}:${rawItem.type}:${JSON.stringify(rawItem)}`;
  } catch {
    return `${agentName}:${rawItem.type}`;
  }
}

type ApprovalResolution = 'approved' | 'rejected' | 'pending';
function resolveApprovalState(
  item: RunToolApprovalItem,
  state: RunState<any, any>,
): ApprovalResolution {
  if (isHostedMcpApprovalItem(item)) {
    return 'pending';
  }

  const rawItem = item.rawItem;
  const toolName =
    item.toolName ??
    ('name' in rawItem && typeof rawItem.name === 'string'
      ? rawItem.name
      : undefined);
  const callId =
    'callId' in rawItem && typeof rawItem.callId === 'string'
      ? rawItem.callId
      : 'id' in rawItem && typeof rawItem.id === 'string'
        ? rawItem.id
        : undefined;

  if (!toolName || !callId) {
    return 'pending';
  }

  const approval = state._context.isToolApproved({ toolName, callId });
  if (approval === true) {
    return 'approved';
  }
  if (approval === false) {
    return 'rejected';
  }
  return 'pending';
}

type AppendContext = {
  seenItems: Set<RunItem>;
  seenApprovalIdentities: Set<string>;
};

function buildAppendContext(existingItems: RunItem[]): AppendContext {
  const seenItems = new Set<RunItem>(existingItems);
  const seenApprovalIdentities = new Set<string>();
  for (const item of existingItems) {
    if (item instanceof RunToolApprovalItem) {
      const identity = getApprovalIdentity(item);
      if (identity) {
        seenApprovalIdentities.add(identity);
      }
    }
  }
  return { seenItems, seenApprovalIdentities };
}

function appendRunItemIfNew(
  item: RunItem,
  target: RunItem[],
  context: AppendContext,
) {
  if (context.seenItems.has(item)) {
    return;
  }
  if (item instanceof RunToolApprovalItem) {
    const identity = getApprovalIdentity(item);
    if (identity) {
      if (context.seenApprovalIdentities.has(identity)) {
        return;
      }
      context.seenApprovalIdentities.add(identity);
    }
  }
  context.seenItems.add(item);
  target.push(item);
}

type TurnFinalizationParams<TContext> = {
  agent: Agent<TContext, any>;
  runner: Runner;
  state: RunState<TContext, Agent<TContext, any>>;
  functionResults: FunctionToolResult<TContext>[];
  originalInput: string | AgentInputItem[];
  newResponse: ModelResponse;
  preStepItems: RunItem[];
  newItems: RunItem[];
  additionalInterruptions?: RunToolApprovalItem[];
};

// Consolidates the logic that determines whether tool results yielded a final answer,
// triggered an interruption, or require the agent loop to continue running.
async function maybeCompleteTurnFromToolResults<TContext>({
  agent,
  runner: _runner,
  state,
  functionResults,
  originalInput,
  newResponse,
  preStepItems,
  newItems,
  additionalInterruptions = [],
}: TurnFinalizationParams<TContext>): Promise<SingleStepResult | null> {
  const toolOutcome = await checkForFinalOutputFromTools(
    agent,
    functionResults,
    state,
    additionalInterruptions,
  );

  if (toolOutcome.isFinalOutput) {
    // Intentional: explicit toolUseBehavior finalization (for example stop_on_first_tool) takes precedence even when other provider-managed work is still pending.
    return new SingleStepResult(
      originalInput,
      newResponse,
      preStepItems,
      newItems,
      {
        type: 'next_step_final_output',
        output: toolOutcome.finalOutput,
      },
    );
  }

  if (toolOutcome.isInterrupted) {
    return new SingleStepResult(
      originalInput,
      newResponse,
      preStepItems,
      newItems,
      {
        type: 'next_step_interruption',
        data: {
          interruptions: toolOutcome.interruptions,
        },
      },
    );
  }

  return null;
}

/**
 * @internal
 * Executes every follow-up action the model requested (function tools, computer actions, MCP flows),
 * appends their outputs to the run history, and determines the next step for the agent loop.
 */
export async function resolveTurnAfterModelResponse<
  TContext,
  TAgent extends Agent<TContext, any>,
>(
  agent: TAgent,
  originalInput: string | AgentInputItem[],
  originalPreStepItems: RunItem[],
  newResponse: ModelResponse,
  processedResponse: ProcessedResponse<TContext>,
  runner: Runner,
  state: RunState<TContext, TAgent>,
  toolErrorFormatter?: ToolErrorFormatter,
  agentToolParentRunConfig?: Partial<RunConfig>,
  errorHandlers?: RunErrorHandlers<TContext, TAgent>,
): Promise<SingleStepResult> {
  // Reuse the same array reference so we can compare object identity when deciding whether to
  // append new items, ensuring we never double-stream existing RunItems.
  const preStepItems = originalPreStepItems;
  const newItems: RunItem[] = [];
  const appendContext = buildAppendContext(originalPreStepItems);
  const appendIfNew = (item: RunItem) =>
    appendRunItemIfNew(item, newItems, appendContext);

  for (const item of processedResponse.newItems) {
    appendIfNew(item);
  }

  // Run function tools and computer actions in parallel; neither depends on the other's side effects.
  // Shell actions mutate the sandbox filesystem, so preserve model order.
  const [functionResults] = await Promise.all([
    executeFunctionToolCalls(
      agent,
      processedResponse.functions,
      runner,
      state,
      toolErrorFormatter,
      agentToolParentRunConfig,
    ),
  ]);

  for (const result of functionResults) {
    if (
      result.type === 'function_output' &&
      Array.isArray(result.interruptions) &&
      result.interruptions.length > 0
    ) {
      continue;
    }
    appendIfNew(result.runItem);
  }

  const additionalInterruptions = collectInterruptions([]);

  if (processedResponse.mcpApprovalRequests.length > 0) {
    await handleHostedMcpApprovals({
      requests: processedResponse.mcpApprovalRequests,
      agent,
      state,
      functionResults,
      appendIfNew,
      resolveApproval: (rawItem) => {
        const providerData =
          rawItem.providerData as ProviderData.HostedMCPApprovalRequest;
        const approvalRequestId = rawItem.id ?? providerData?.id;
        if (!approvalRequestId) {
          return undefined;
        }
        return state._context.isToolApproved({
          toolName: rawItem.name,
          callId: approvalRequestId,
        });
      },
    });
  }

  // process handoffs
  if (processedResponse.handoffs.length > 0) {
    return await executeHandoffCalls(
      agent,
      originalInput,
      preStepItems,
      newItems,
      newResponse,
      processedResponse.handoffs as ToolRunHandoff[],
      runner,
      state._context,
    );
  }

  const completedStep = await maybeCompleteTurnFromToolResults({
    agent,
    runner,
    state,
    functionResults,
    originalInput,
    newResponse,
    preStepItems,
    newItems,
    additionalInterruptions,
  });

  if (completedStep) {
    return completedStep;
  }

  // If the model issued any tool calls or handoffs in this turn,
  // we must NOT treat any assistant message in the same turn as the final output.
  // We should run the loop again so the model can see the tool results and respond.
  if (processedResponse.hasToolsOrApprovalsToRun()) {
    return new SingleStepResult(
      originalInput,
      newResponse,
      preStepItems,
      newItems,
      { type: 'next_step_run_again' },
    );
  }
  // No tool calls/actions in this turn; safe to consider a plain assistant message as final.
  const messageItems = newItems.filter(
    (item) => item instanceof RunMessageOutputItem,
  );

  // we will use the last content output as the final output
  const potentialFinalOutput =
    messageItems.length > 0
      ? getTextFromOutputMessage(messageItems[messageItems.length - 1]!.rawItem)
      : undefined;

  // Keep looping if any tool output placeholders still require an approval follow-up.
  const hasPendingToolsOrApprovals =
    functionResults.some(
      (result) => result.runItem instanceof RunToolApprovalItem,
    ) || additionalInterruptions.length > 0;

  if (!hasPendingToolsOrApprovals && messageItems.length > 0) {
    const refusal = getRefusalFromOutputMessage(
      messageItems[messageItems.length - 1]!.rawItem,
    );
    if (refusal && typeof potentialFinalOutput === 'undefined') {
      const refusalError = new ModelRefusalError(refusal, state);
      const generatedItems = preStepItems.concat(newItems);
      const runData: RunErrorData<TContext, TAgent> = {
        input: originalInput,
        newItems: generatedItems,
        history: getTurnInput(
          originalInput,
          generatedItems,
          state._reasoningItemIdPolicy,
        ),
        output: getTurnInput([], generatedItems, state._reasoningItemIdPolicy),
        rawResponses: state._modelResponses,
        lastAgent: agent,
        state,
      };
      const handlerResult = await resolveRunErrorHandler({
        error: refusalError,
        errorHandlers,
        context: state._context,
        runData,
      });
      if (!handlerResult) {
        throw refusalError;
      }

      const outputText = formatRunErrorFinalOutput(
        agent,
        handlerResult.finalOutput,
      );
      if (handlerResult.includeInHistory !== false) {
        newItems.push(createRunErrorFinalOutputItem(agent, outputText));
      }
      return new SingleStepResult(
        originalInput,
        newResponse,
        preStepItems,
        newItems,
        {
          type: 'next_step_final_output',
          output: outputText,
        },
      );
    }
  }

  // if there is no output we just run again
  if (typeof potentialFinalOutput === 'undefined') {
    return new SingleStepResult(
      originalInput,
      newResponse,
      preStepItems,
      newItems,
      { type: 'next_step_run_again' },
    );
  }

  if (!hasPendingToolsOrApprovals) {
    if (agent.outputType === 'text') {
      return new SingleStepResult(
        originalInput,
        newResponse,
        preStepItems,
        newItems,
        {
          type: 'next_step_final_output',
          output: potentialFinalOutput,
        },
      );
    }

    if (agent.outputType !== 'text' && potentialFinalOutput) {
      // Structured output schema => always leads to a final output if we have text.
      const { parser } = getSchemaAndParserFromInputType(
        agent.outputType,
        'final_output',
      );
      const [error] = await safeExecute(() => parser(potentialFinalOutput));
      if (error) {
        const outputErrorMessage = formatFinalOutputTypeError(error);
        addErrorToCurrentSpan({
          message: outputErrorMessage,
          data: {
            error: String(error),
          },
        });
        throw new ModelBehaviorError(outputErrorMessage);
      }

      return new SingleStepResult(
        originalInput,
        newResponse,
        preStepItems,
        newItems,
        {
          type: 'next_step_final_output',
          output: potentialFinalOutput,
        },
      );
    }
  }

  return new SingleStepResult(
    originalInput,
    newResponse,
    preStepItems,
    newItems,
    { type: 'next_step_run_again' },
  );
}

function formatFinalOutputTypeError(error: unknown): string {
  // Surface structured output validation hints without echoing potentially large or sensitive payloads.
  try {
    if (error instanceof z.ZodError) {
      const issue = error.issues[0];
      if (issue) {
        const issuePathParts = Array.isArray(issue.path) ? issue.path : [];
        const issuePath =
          issuePathParts.length > 0
            ? issuePathParts.map((part) => String(part)).join('.')
            : '(root)';
        const message = truncateForDeveloper(issue.message ?? '');
        return `Invalid output type: final assistant output failed schema validation at "${issuePath}" (${message}).`;
      }
      return 'Invalid output type: final assistant output failed schema validation.';
    }

    if (error instanceof Error && error.message) {
      return `Invalid output type: ${truncateForDeveloper(error.message)}`;
    }
  } catch {
    // Swallow formatting errors so we can return a generic message below.
  }

  return 'Invalid output type: final assistant output did not match the expected schema.';
}

function truncateForDeveloper(message: string, maxLength = 160): string {
  const trimmed = message.trim();
  if (!trimmed) {
    return 'Schema validation failed.';
  }
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return `${trimmed.slice(0, maxLength - 3)}...`;
}
